import React from 'react';
import { Collapse, Empty, Badge } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import { WORKFLOW_CONFIG, WORKFLOW_COLORS, UI_TEXT } from 'src/enums/EOutbound';
import { WorkflowItem } from './WorkflowItem';

const { Panel } = Collapse;

interface WorkflowSectionProps {
	workflows: any[];
	type: string;
	selectedWorkflows: { [key: string]: boolean };
	onWorkflowToggle: (workflowId: string, workflowType: string, enabled: boolean) => void;
	icon: React.ReactNode;
	activeKeys: string[];
	setActiveKeys: (keys: string[]) => void;
	selectedCount?: number;
	totalCount?: number;
}

export const WorkflowSection: React.FC<WorkflowSectionProps> = ({
	workflows,
	type,
	selectedWorkflows,
	onWorkflowToggle,
	icon,
	activeKeys,
	setActiveKeys,
	selectedCount = 0,
	totalCount = 0,
}) => {
	const config = WORKFLOW_CONFIG[type as keyof typeof WORKFLOW_CONFIG];

	if (!config) {
		return null;
	}

	const colors = WORKFLOW_COLORS[config.color as keyof typeof WORKFLOW_COLORS];

	return (
		<Collapse
			activeKey={activeKeys}
			onChange={setActiveKeys}
			expandIcon={({ isActive }) => (
				<CaretRightOutlined rotate={isActive ? 90 : 0} className="text-gray-500" />
			)}
			className="[&_.ant-collapse-item]:!border-0 [&_.ant-collapse-item:last-child]:!border-b-0"
			bordered={false}
		>
			<Panel
				key={type}
				header={
					<div className="flex items-center justify-between w-full pr-4">
						<div className="flex items-center gap-3">
							<div className={`p-2 rounded-lg ${colors.bg100} relative`}>
								<span className={colors.text600}>{icon}</span>
								{selectedCount > 0 && (
									<Badge
										count={selectedCount}
										size="small"
										className="absolute -top-1 -right-1"
										style={{ backgroundColor: '#52c41a' }}
									/>
								)}
							</div>
							<div>
								<div className="flex items-center gap-2">
									<h3 className="font-semibold text-gray-900 m-0">{config.title}</h3>
									{selectedCount > 0 && (
										<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
											{selectedCount} selected
										</span>
									)}
								</div>
								<p className="text-sm text-gray-600 m-0">{config.description}</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500">
								{totalCount} {UI_TEXT.AVAILABLE}
							</span>
							{selectedCount > 0 && (
								<span className="text-sm font-medium text-green-600">
									({selectedCount}/{totalCount})
								</span>
							)}
						</div>
					</div>
				}
				className="[&_.ant-collapse-header]:!p-5 [&_.ant-collapse-header]:!bg-gray-50 [&_.ant-collapse-header]:!border-b [&_.ant-collapse-header]:!border-gray-200 [&_.ant-collapse-content-box]:!p-6 [&_.ant-collapse-content-box]:!pt-2 !border-0"
			>
				{workflows.length > 0 ? (
					<div className="space-y-3 pt-2">
						{workflows.map((workflow) => {
							const isSelected = selectedWorkflows[workflow.id] || false;

							return (
								<div key={workflow.id}>
									{workflow.isCustom ? (
										<div className="p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-3">
													<div className={`p-2 rounded-lg ${colors.bg100}`}>
														<span className={colors.text600}>{icon}</span>
													</div>
													<div>
														<h4 className="font-medium text-gray-900 mb-1">{workflow.name}</h4>
														<p className="text-sm text-gray-600 mb-0">
															{workflow.description || workflow.scenario}
														</p>
														{workflow.data?.questions && (
															<p className="text-xs text-gray-500 mt-1">
																{workflow.data.questions.length} questions configured
															</p>
														)}
														<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-1">
															Custom
														</span>
													</div>
												</div>
												<div className="flex items-center gap-3">
													{workflow.enabled && (
														<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
															Enabled
														</span>
													)}
													<label className="relative inline-flex items-center cursor-pointer">
														<input
															type="checkbox"
															className="sr-only peer"
															checked={isSelected}
															onChange={(e) => {
																e.stopPropagation();
																onWorkflowToggle(workflow.id, type, e.target.checked);
															}}
														/>
														<div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
													</label>
												</div>
											</div>
										</div>
									) : (
										<WorkflowItem
											workflow={workflow}
											type={type}
											isSelected={isSelected}
											onToggle={(workflowId: string, workflowType: string, enabled: boolean) =>
												onWorkflowToggle(workflowId, workflowType, enabled)
											}
											icon={icon}
										/>
									)}
								</div>
							);
						})}
					</div>
				) : (
					<Empty description={`No ${config.title.toLowerCase()} configured`} className="py-8" />
				)}
			</Panel>
		</Collapse>
	);
};
